import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getTokenForBrightspeedAPI } from "../../../../shared/hooks/useFetchLogin";
import API_URLS from "../../Utils/axr";
import { errorConditionGTM } from "../../Utils/googleTags";
import DwLogutil from "../../Utils/DwLogutil";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import { RootState } from "../store";
import { PURGE } from "redux-persist";

type brightspeedAPITokenInitialStateType = {
  data: any;
  loading: boolean;
  error: any;
  headers: any;
  tokenExpiresAt: number | null;
};
const initialState: brightspeedAPITokenInitialStateType = {
  data: null,
  loading: false,
  error: null,
  tokenExpiresAt: null,
  headers: {
    brspdChId: "web-acm",
    channel: "web-acm",
    sessionId: sessionStorage?.getItem("sessionId") || "",
    siteId: localStorage.getItem("headersSiteId") || "",
    billingType: null,
    networkType: null,
    billingSystem: null,
    flowType: null,
    nonAuthFlowType: null,
    Authorization: null,
    uniqueId: null,
    acode: null,
    cid: null,
  },
};

// Fallback TTL used only when the server doesn't return a usable `expires_in`.
const DEFAULT_TOKEN_TTL_SECONDS = 28 * 60;
// Refresh this many seconds before the server-side expiry so in-flight
// requests don't land with a just-expired token.
const TOKEN_REFRESH_MARGIN_SECONDS = 60;

// Dedupes concurrent dispatches into one digital-id call.
let pendingTokenRequest: Promise<any> | null = null;

export const getBrightspeedToken = createAsyncThunk<any, string | void, { state: RootState }>(
  "brightspeedAPI/token",
  async (flow, { getState, dispatch }) => {
    const state = getState();
    const existingToken = state?.brightspeedToken?.data;
    const cachedAccessToken = existingToken?.access_token || existingToken?.id;
    // Read expiry from the top-level slice field. It is persisted by
    // redux-persist alongside the token data, so it survives page reloads
    // and the B2C round-trip.
    const expiryMs = state?.brightspeedToken?.tokenExpiresAt ?? 0;
    // Reuse the cached token until it nears expiry. `tokenExpiresAt` is
    // stamped at (server expiry - TOKEN_REFRESH_MARGIN_SECONDS), so this
    // effectively refreshes ~1 minute before the real expiry.
    if (cachedAccessToken && expiryMs > Date.now()) {
      const normalizedCachedToken = {
        ...existingToken,
        access_token: cachedAccessToken,
        tokenExpiresAt:
          typeof existingToken?.tokenExpiresAt === "number"
            ? existingToken.tokenExpiresAt
            : expiryMs,
      };
      const expectedAuth = `Bearer ${cachedAccessToken}`;
      if (state?.brightspeedToken?.headers?.Authorization !== expectedAuth) {
        dispatch(
          updateHeaders({
            Authorization: expectedAuth,
          })
        );
      }
      return normalizedCachedToken;
    }
    // If a fetch is already running, reuse it rather than starting a second one.
    if (pendingTokenRequest) {
      return pendingTokenRequest;
    }
    const fetchToken = async () => {
      try {
        const params = {
          "": [API_URLS?.brightspeedGetTokenAPI],
        };
        const headers = {};
        const response = await getTokenForBrightspeedAPI(params, {}, {
          ...headers,
          ...(flow && { flowType: flow }),
        });

        if (response?.data) {
          const expiresInSeconds =
            parseInt(response.data.expires_in, 10) || DEFAULT_TOKEN_TTL_SECONDS;
          const expiresAtMs =
            Date.now() + (expiresInSeconds - TOKEN_REFRESH_MARGIN_SECONDS) * 1000;
          response.data.expires_in = expiresInSeconds;
          response.data.expiryDate = new Date(expiresAtMs).toISOString();
          response.data.tokenExpiresAt = expiresAtMs;
        }
        if (response?.data?.id) {
          response.data.access_token = response?.data?.id;

          DwLogutil({
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${response?.data?.access_token}`,
            }, stepName: EC_DW_LOGGING_STEPS?.digital_id_api_success
          });
        }
        return response.data;
      } catch (error) {
        DwLogutil({stepName: EC_DW_LOGGING_STEPS?.digital_id_api_failed, error: error});
        throw error;
      }
    };

    pendingTokenRequest = fetchToken();
    try {
      return await pendingTokenRequest;
    } finally {
      pendingTokenRequest = null;
    }
  }
);

const brightspeedTokenSlice = createSlice({
  name: "brightspeed/token",
  initialState,
  reducers: {
    resetHeaders: (state) => {
      state.headers = initialState.headers;
    },
    resetToken: (state) => {
      state.data = null;
      state.tokenExpiresAt = null;
    },
    setHeaders: (state, action: PayloadAction<any>) => {
      state.headers = action.payload;
    },
    updateHeaders: (state, action: PayloadAction<any>) => {
      state.headers = {
        ...state.headers,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBrightspeedToken.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBrightspeedToken.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
      state.tokenExpiresAt =
        typeof action.payload?.tokenExpiresAt === "number"
          ? action.payload.tokenExpiresAt
          : null;
    });
    builder.addCase(getBrightspeedToken.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "get token APi failed",
        action.error?.message,
        action?.type
      );
      state.data = null;
      state.loading = false;
      state.error = action.error;
      state.tokenExpiresAt = null;
    });
    builder.addCase(PURGE, () => {
          return  initialState;
     });
  },
});

export const { setHeaders, resetHeaders, updateHeaders, resetToken } =
  brightspeedTokenSlice.actions;
export default brightspeedTokenSlice.reducer;